import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { FaLock, FaSave } from "react-icons/fa";
import { Container } from "reactstrap";
import { GamesAPI } from "../../../../api/games";
import { SettingsAPI } from "../../../../api/settings";
import { SessionStorage } from "../../../../utils/storage";
import { TOKEN_KEY } from "../../../../constants/appkeys";
import SPINNER from "../../../../components/Spinner";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import SectionWrapper from "../../../../components/SectionWrapper";

interface FormDataProps {
  refLink: string;
  picture: string;
  show: boolean;
}

const initialFormData: FormDataProps = {
  refLink: "",
  picture: "",
  show: true,
};
const Setting = () => {
  document.title = "Casino24 - Settings";
  const [formData, setFormData] = useState<FormDataProps>(initialFormData);
  const [picture, setPicture] = useState<File>();
  const [loading, setLoading] = useState<boolean>(false);

  const fetchSettings = async () => {
    setLoading(true);
    const res = await SettingsAPI.getData();
    if (res.data) {
      setFormData(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleToggle = () => {
    setFormData((prevData) => ({
      ...prevData,
      show: !formData.show,
    }));
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;

    if (name === "picture" && files && files.length > 0) {
      setPicture(files[0]);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    let pictureUrl = formData.picture;
    if (picture) {
      const res = (await GamesAPI.uploadImage(picture)) as {
        url?: string;
        success?: boolean;
      };
      if (res.success && res.url) {
        pictureUrl = res.url;
      } else {
        alert("Something went wrong P. Try Again!");
      }
    }

    const dataToSend = {
      refLink: formData.refLink,
      picture: pictureUrl,
      show: formData.show,
    };

    try {
      const res = await SettingsAPI.uploadData(dataToSend);
      if (res.data.id) {
        setFormData(res.data);
      }
    } catch (error) {
      alert("Something went wrong D! Try Again.");
    }

    setLoading(false);
  };

  return (
    <Container
      fluid
      className="p-0 m-0 flex flex-col text-black w-full min-h-screen bg-[#eeeeee] relative"
    >
      {/* Header */}
      <section className="p-4 text-[3xl] w-full h-fit bg-white font-bold">
        <h2>Settings</h2>
      </section>

      {/* Form Section */}
      <section className="flex-1 p-4 justify-center items-center">
        {/* Form */}
        <form
          className="bg-white h-full w-full flex flex-col p-3"
          onSubmit={handleSubmit}
        >
          {/* Information */}
          <SectionWrapper header="Deal of the Week">
            <ToggleSwitch
              className="self-end mb-2"
              show={formData.show}
              toggle={handleToggle}
            />
            {/* Ref Link */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="refLink"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Ref Link
              </label>
              <input
                disabled={!formData.show}
                type="text"
                id="refLink"
                name="refLink"
                onChange={handleInputChange}
                className={`p-2 w-full border text-[0.8rem] col-span-5 ${
                  !formData.show && "text-gray-300 cursor-not-allowed"!
                }`}
                required
                value={formData.refLink}
              />
            </div>
            {/* Profile Picture */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="picture"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Cover Image
              </label>
              <input
                disabled={!formData.show}
                type="file"
                id="picture"
                name="picture"
                accept="image/*"
                onChange={handleInputChange}
                required={formData.picture === ""}
                className={`p-2 w-full border text-[0.8rem] col-span-5 ${
                  !formData.show && "text-gray-300 cursor-not-allowed"!
                }`}
              />
            </div>
            {formData.picture && (
              <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
                <div />
                <div className="p-2 w-full col-span-5">
                  <img
                    alt="Preview"
                    src={formData.picture}
                    className={`w-full h-32 md:h-40 lg:h-64 object-fit ${
                      !formData.show ? "opacity-50" : "opacity-100"
                    } transition-opacity duration-300`}
                  />
                </div>
              </div>
            )}
          </SectionWrapper>

          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="bg-green-600 text-white p-2 px-4 rounded-md mx-2 flex items-center"
            >
              <FaSave className="mr-2" /> Save
            </button>
          </div>
        </form>

        <div className="bg-white h-full w-full flex flex-col p-3 mt-2">
          <SectionWrapper header="User Options">
            <div
              onClick={() => {
                SessionStorage.set(TOKEN_KEY, "");
                window.location.replace("/vips/login");
              }}
              className="flex justify-center items-center w-fit border py-2 px-4 self-end hover:bg-gray-400/30 cursor-pointer select-none"
            >
              <FaLock className="mr-2" />
              Logout
            </div>
          </SectionWrapper>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-gray-300/30 flex items-center justify-center backdrop-blur-sm">
            <SPINNER />
          </div>
        )}
      </section>
    </Container>
  );
};

export default Setting;
